import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { activeMembership, useSession } from '../../lib/store';
import { Card, Empty, Notice, PageHeading, Spinner } from '../../components/Shell';
import { UpdateCard } from '../../components/VisitUpdate';
import { BookingStamp, Stamp, VaccinationStamp } from '../../components/Stamp';
import { dateRange, money, relativeDays, titleCase } from '../../lib/format';
import type { AgreementSummary, Booking, Pet, PetDocument, VisitReport } from '../../lib/types';

interface PetWithStatus {
  pet: Pet;
  vaccinesCurrent: boolean;
  pendingDocuments: number;
}

/**
 * The client's home. It leads with what is outstanding, because the whole
 * point of the product is that nobody has to chase paperwork by text message.
 */
export function PortalHome() {
  const session = useSession();
  const membership = activeMembership(session);
  const tenantId = session.tenant?.tenantId;

  const [pets, setPets] = useState<PetWithStatus[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [agreements, setAgreements] = useState<AgreementSummary[]>([]);
  const [latestUpdate, setLatestUpdate] = useState<VisitReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sendingAgreement, setSendingAgreement] = useState(false);

  useEffect(() => {
    if (!tenantId) return;

    void (async () => {
      try {
        const [petResult, bookingResult, agreementResult] = await Promise.all([
          api<{ pets: Pet[] }>(`/tenants/${tenantId}/pets`),
          api<{ bookings: Booking[] }>(`/tenants/${tenantId}/bookings`),
          api<{ agreements: AgreementSummary[] }>(`/tenants/${tenantId}/agreements`),
        ]);

        // Vaccination status lives with the documents, so fetch per pet.
        const withStatus = await Promise.all(
          petResult.pets.map(async (pet) => {
            try {
              const docs = await api<{ documents: PetDocument[]; vaccinations: { current: boolean } }>(
                `/tenants/${tenantId}/pets/${pet.petId}/documents`
              );
              return {
                pet,
                vaccinesCurrent: docs.vaccinations.current,
                pendingDocuments: docs.documents.filter((d) => d.reviewStatus === 'pending').length,
              };
            } catch {
              return { pet, vaccinesCurrent: false, pendingDocuments: 0 };
            }
          })
        );

        setPets(withStatus);
        setBookings(bookingResult.bookings);
        setAgreements(agreementResult.agreements);

        // While a pet is away, the newest update is the only thing on this
        // page anyone actually opens it for. It was two taps down inside the
        // booking; it belongs at the top, and it is fetched only for the stay
        // that is happening now so a client with four years of history pays
        // for one request rather than forty.
        const now = new Date().toISOString().slice(0, 10);
        const inCare = bookingResult.bookings.find(
          (b) =>
            ['confirmed', 'in_progress'].includes(b.status) && b.startDate <= now && b.endDate >= now
        );
        if (inCare) {
          try {
            const result = await api<{ reports: VisitReport[] }>(
              `/tenants/${tenantId}/bookings/${inCare.bookingId}/reports`
            );
            setLatestUpdate(result.reports[0] ?? null);
          } catch {
            // A missing update is not an error worth showing on this page.
          }
        }
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Your file could not be loaded.');
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId]);

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-5 py-16">
        <Spinner label="Opening your file" />
      </div>
    );
  }

  const signedAgreement = agreements.find((a) => a.status === 'signed');
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings
    .filter((b) => b.endDate >= today && !['cancelled', 'declined', 'completed'].includes(b.status))
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
  const past = bookings.filter((b) => !upcoming.includes(b)).slice(0, 6);

  const needsVaccines = pets.filter((p) => !p.vaccinesCurrent);
  const needsAgreement = !signedAgreement;

  const sendAgreement = () => {
    if (!tenantId) return;
    setSendingAgreement(true);
    void api<{ signUrl: string }>(`/tenants/${tenantId}/agreements`, { method: 'POST', body: {} })
      .then((result) => {
        window.location.href = result.signUrl;
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'The agreement could not be opened.');
        setSendingAgreement(false);
      });
  };

  return (
    <div className="mx-auto max-w-4xl px-5 pb-24 pt-12">
      <PageHeading
        eyebrow={session.tenant?.businessName}
        title={`Hello, ${session.user?.name.split(' ')[0] ?? 'there'}`}
        lead={`Everything ${session.tenant?.businessName ?? 'your sitter'} needs to look after them, all in one place.`}
        actions={
          <>
            <Link className="btn btn-secondary" to="/portal/details">
              My details
            </Link>
            <Link className="btn btn-primary" to="/portal/book">
              Book a stay
            </Link>
          </>
        }
      />

      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      {/* --- The newest update -------------------------------------------- */}
      {/* Above the outstanding block deliberately. Nothing is outstanding while
          a pet is actually away, and if a photograph of them arrived this
          morning it outranks every other thing on this page. */}
      {latestUpdate && (
        <section className="mb-10">
          <h2 className="mb-4 text-2xl">{updateHeading(latestUpdate, pets)}</h2>
          <UpdateCard report={latestUpdate} timezone={session.tenant?.timezone} />
        </section>
      )}

      {/* --- What is outstanding ------------------------------------------ */}
      {(needsAgreement || needsVaccines.length > 0 || pets.length === 0) && (
        <Card tint="yellow" className="mb-10 p-6">
          <h2 className="font-display text-xl font-bold">A couple of things first</h2>
          <ul className="mt-4 space-y-4">
            {pets.length === 0 && (
              <li className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-sm">
                  Tell us about your first pet, so they are never a stranger on day one.
                </span>
                <Link className="btn btn-secondary" to="/portal/pets/new">
                  Add a pet
                </Link>
              </li>
            )}
            {needsAgreement && pets.length > 0 && (
              <li className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-sm">
                  Sign this season&rsquo;s services agreement. It takes about five minutes on your phone.
                </span>
                <button type="button" className="btn btn-secondary" onClick={sendAgreement} disabled={sendingAgreement}>
                  {sendingAgreement ? 'Opening' : 'Read and sign'}
                </button>
              </li>
            )}
            {needsVaccines.map(({ pet }) => (
              <li key={pet.petId} className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-sm">
                  Upload {pet.name}&rsquo;s vaccination record. A photo of the card is fine.
                </span>
                <Link className="btn btn-secondary" to={`/portal/pets/${pet.petId}`}>
                  Upload
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* --- Pets ---------------------------------------------------------- */}
      <section className="mb-12">
        <div className="mb-5 flex items-end justify-between">
          <h2 className="text-2xl">My pets</h2>
          <Link className="btn btn-quiet" to="/portal/pets/new">
            Add a pet
          </Link>
        </div>

        {pets.length === 0 ? (
          <Empty
            title="Nobody here yet"
            body="Feeding times, medication, the things they love and the things they hate. All of it before the first stay, so none of it is a guess."
            action={{ label: 'Add your first pet', to: '/portal/pets/new' }}
          />
        ) : (
          <div className="grid gap-5 sm:grid-cols-2">
            {pets.map(({ pet, vaccinesCurrent, pendingDocuments }) => (
              <Link
                key={pet.petId}
                to={`/portal/pets/${pet.petId}`}
                className="card block p-5 transition-shadow hover:shadow-[0_2px_0_var(--color-stamp)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-2xl font-extrabold">{pet.name}</h3>
                    <p className="typed mt-0.5 text-sm text-[color:var(--color-ink-soft)]">
                      {[titleCase(pet.species), pet.breed, pet.ageYears ? `${pet.ageYears} yrs` : null]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                  </div>
                  <VaccinationStamp current={vaccinesCurrent} flat />
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {pet.medical.medicationRequired && (
                    <Stamp tone="pending" flat>
                      On medication
                    </Stamp>
                  )}
                  {pet.assessment.status === 'passed' && (
                    <Stamp tone="ok" flat>
                      Assessment passed
                    </Stamp>
                  )}
                  {pendingDocuments > 0 && (
                    <Stamp tone="quiet" flat>
                      {pendingDocuments} awaiting review
                    </Stamp>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* --- Bookings ------------------------------------------------------ */}
      <section className="mb-12">
        <h2 className="mb-5 text-2xl">Upcoming</h2>
        {upcoming.length === 0 ? (
          <Empty
            title="No stays booked"
            body={`Pick your dates and ${session.tenant?.businessName ?? 'your sitter'} will come back to you.`}
            action={{ label: 'Book a stay', to: '/portal/book' }}
          />
        ) : (
          <ul className="space-y-3">
            {upcoming.map((booking) => (
              <li key={booking.bookingId}>
                <Link
                  to={`/portal/bookings/${booking.bookingId}`}
                  className="card flex flex-wrap items-center justify-between gap-4 p-4 transition-shadow hover:shadow-[0_2px_0_var(--color-stamp)]"
                >
                  <div>
                    <p className="font-display text-lg font-bold">
                      {dateRange(booking.startDate, booking.endDate, session.tenant?.timezone)}
                    </p>
                    <p className="typed text-sm text-[color:var(--color-ink-soft)]">
                      {titleCase(booking.serviceCode)} &middot; {relativeDays(booking.startDate)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="typed">{money(booking.quote.total)}</span>
                    <BookingStamp status={booking.status} flat />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {past.length > 0 && (
        <section>
          <h2 className="mb-5 text-2xl">Stays they have had</h2>
          <ul className="space-y-2">
            {past.map((booking) => (
              <li key={booking.bookingId}>
                <Link
                  to={`/portal/bookings/${booking.bookingId}`}
                  className="flex flex-wrap items-center justify-between gap-4 border-b border-[color:var(--color-rule)] py-3 text-sm hover:text-[color:var(--color-stamp)]"
                >
                  <span className="typed">
                    {dateRange(booking.startDate, booking.endDate, session.tenant?.timezone)}
                  </span>
                  <span className="flex items-center gap-4">
                    <span className="typed text-[color:var(--color-ink-soft)]">{money(booking.quote.total)}</span>
                    <BookingStamp status={booking.status} flat />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {signedAgreement && (
        <p className="mt-12 font-mono text-xs text-[color:var(--color-ink-faint)]">
          {signedAgreement.season} agreement signed
          {signedAgreement.signedAt ? ` on ${signedAgreement.signedAt.slice(0, 10)}` : ''}.{' '}
          {membership?.clientId && (
            <a
              className="underline"
              href={`${window.location.origin}/portal/details`}
            >
              Download a copy
            </a>
          )}
        </p>
      )}
    </div>
  );
}

/**
 * "Luna and Piper today" reads like a person wrote it; "How they are doing"
 * reads like a database did. The neutral wording is the fallback for an update
 * posted against a pet since archived off this client's list.
 */
function updateHeading(report: VisitReport, pets: PetWithStatus[]): string {
  const names = report.petIds
    .map((petId) => pets.find((entry) => entry.pet.petId === petId)?.pet.name)
    .filter((name): name is string => Boolean(name));

  if (names.length === 0) return 'How they are doing';
  const joined =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${joined} today`;
}
